import { schemas } from '#oaf_schema.registry';
import { createStore } from '@oneappexchange/lwc-wired';
import equalityFn from 'zustand/shallow';
import { IS_DEVELOPMENT } from '~/config';
import { deepFreeze } from '~/modules/utilities/objectUtilities';
import { PUBLISHER_ROUTE, routerStore, selectMatch } from '../routerStore';
import { publisherStore, selectJapanesePublisher, selectLogoSetItems, selectPublisherId } from './publisherStore';
// Exported here so it can be imported by publisherEditorStore for its internal business logic
export const initialState = deepFreeze({
  publisher: schemas['publisher/localizations/jp'].new({
    status: 'Inactive'
  })
});
export const japanesePublisherEditorStore = createStore(initialState, (set, get) => ({
  reset() {
    set(function reset(draft) {
      // When resetting the JP publisher always preserve the external_id field to ensure sync to Tz doesn't break
      draft.publisher = {
        ...initialState.publisher,
        external_id: get().publisher.external_id
      };
    });
  },
  toggle() {
    set(function toggle(draft) {
      draft.publisher.status = draft.publisher.status === 'Active' ? 'Inactive' : 'Active';
    });
  },
  setActive() {
    set(function setActive(draft) {
      draft.publisher.status = 'Active';
    });
  },
  setPublisher(publisher = initialState.publisher) {
    set(function setPublisher(draft) {
      draft.publisher = publisher;
      // Upsert the status field if the marketplace location is missing it.
      // If this publisher has any existing Japanese Listings, assume it is active
      // If a marketplace localization publisher (jp in this case) has an external_id than it likely already synced to Tz, assume it is active
      if (!draft.publisher.status) {
        draft.publisher.status = publisherStore.getState().hasJapaneseListings || publisher.external_id ? 'Active' : 'Inactive';
      }
    });
  },
  setSmallLogoUrl(mediaId) {
    set(function setSmallLogoUrl(draft) {
      const items = selectLogoSetItems(draft, true);
      const logo = items.find(test => test.logoType === 'Logo');
      if (!logo) {
        items.push(schemas['publisher/plugins/PublisherLogo/PublisherLogoItem'].new({
          logoType: 'Logo',
          mediaId
        }));
      } else {
        logo.mediaId = mediaId;
      }
    });
  },
  setName(name) {
    set(function setName(draft) {
      draft.publisher.name = name;
    });
  },
  setWebsite(website) {
    set(function setWebsite(draft) {
      draft.publisher.website = website;
    });
  },
  setEmail(email) {
    set(function setEmail(draft) {
      draft.publisher.email = email;
    });
  },
  setHQLocation(hQLocation) {
    set(function setHQLocation(draft) {
      draft.publisher.hQLocation = hQLocation;
    });
  },
  setDescription(description) {
    set(function setDescription(draft) {
      draft.publisher.description = description;
    });
  },
  setPhoneNumber(phone) {
    set(function setPhoneNumber(draft) {
      draft.publisher.phoneNumber = phone;
    });
  }
}), 'JAPANESE_PUBLISHER_EDITOR', IS_DEVELOPMENT);
export function selectJpPublisher(state) {
  return state.publisher;
}
export function selectStatus(state) {
  var _state$publisher$stat, _state$publisher;
  return (_state$publisher$stat = (_state$publisher = state.publisher) === null || _state$publisher === void 0 ? void 0 : _state$publisher.status) !== null && _state$publisher$stat !== void 0 ? _state$publisher$stat : 'Inactive';
}

// Initialize the store only when we have a publisher and we're in the right page (this logic largely mirrors the one in publisherEditorStore)
export function checkShouldInitialize() {
  const {
    publisher
  } = publisherStore.getState();
  const {
    url
  } = routerStore.getState();
  if (!publisher || !url || !PUBLISHER_ROUTE.includes(url)) {
    japanesePublisherEditorStore.reset();
    return;
  }
  japanesePublisherEditorStore.setPublisher(selectJapanesePublisher(publisherStore.getState()));
}
routerStore.subscribe(checkShouldInitialize, selectMatch, equalityFn);
publisherStore.subscribe(checkShouldInitialize, selectPublisherId);

// This module does not load until the publisher editor page is loaded, so check if we should initialize this store as soon as the module loads
checkShouldInitialize();